import {
  createContext,
  useEffect,
  useState,
  useCallback,
  useContext,
} from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { ErrorContext } from "./ErrorContext";
import FiCurveApi from "../tools/ficurve-api";
import { jwtDecode } from "jwt-decode";

export const UserInfoContext = createContext({
  userInfo: null,
  loadingUserInfo: false,
  isEmployee: () => false,
  hasNationalPrivatePlacements: () => false
});

const ADMIN_ORGANIZATION_NAME = "Admin";
const ROLES_NAMESPACE = "https://primary.splinedata.com/roles"

export function UserInfoProvider({ children }) {
  const { logout, getAccessTokenSilently } = useAuth0();
  const [userInfo, setUserInfo] = useState(null);
  const [loadingUserInfo, setLoadingUserInfo] = useState(false);
  const { setActiveError } = useContext(ErrorContext);

  useEffect(() => {
    fetchUserInfo();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchUserInfo = useCallback(async () => {
    setLoadingUserInfo(true);
    const accessToken = await getAccessTokenSilently();
    const api = new FiCurveApi(accessToken);
    api.runGetUserInfo(
      (response) => {
        setLoadingUserInfo(false);
        setUserInfo({
          ...response,
          roles: getRoles(accessToken)
        });
      },
      (err) => {
        console.debug(err);
        setLoadingUserInfo(false);
        setActiveError(
          `There was an error communicating with the API: ${err.message ?? err}`
        );
      }
    );
  }, [getAccessTokenSilently, setActiveError]);

  const onLogout = () => {
    localStorage.removeItem("acknowledge");
    localStorage.removeItem("nationalPrivatePlacementsFilters");
    logout({ logoutParams: { returnTo: window.location.origin } });
  };

  function getDisplayUserName() {
    if (!userInfo || !userInfo.firstName) return "N/A";
    return `${userInfo.firstName} ${userInfo.lastName}`;
  }

  function getDisplayOrganization() {
    if (!userInfo || !userInfo.organization) return "N/A";
    return userInfo.organization.name;
  }

  function getDisplayTeam() {
    if (!userInfo) return "N/A";
    if (userInfo.isAdmin) return ADMIN_ORGANIZATION_NAME;
    if (!userInfo.team || !userInfo.team.name) return "N/A";
    return userInfo.team.name;
  }

  function getRoles(token) {
    if (!token) return [];
    const decoded = jwtDecode(token);
    return decoded[ROLES_NAMESPACE] || [];
  }

  function isEmployee() {
    return userInfo && userInfo.organization && userInfo.organization.id === "ab833fea-807f-4118-b413-ae8e344201e3"
  }

  function hasNationalPrivatePlacements() {
    return !!(userInfo && userInfo.organization && userInfo.organization.hasNationalPrivatePlacement);
  }

  return (
    <UserInfoContext.Provider value={{
      loadingUserInfo, userInfo, onLogout, getDisplayUserName,
      getDisplayOrganization, getDisplayTeam, getRoles, isEmployee,
      hasNationalPrivatePlacements
    }}>
      {children}
    </UserInfoContext.Provider>
  );
}
